/**
 * Todo think through the interactions
 * approach A
 * - one row selected is single select (checkbox is also ticked if multiselect is available)
 * - if no edits and second checkbox is selected then we show the multi-select detail panel if available
 * - if edits and a second checkbox is ticked then we show save prompt
 * ---alternate - maybe we disable checkboxes once edit mode is entered on single select (this probably works)
 * approach B
 * - multiselect mode doesn't do navigation style at all
 * - manually enter "batch" or multiselect mode
 *
 *
 * */
import { html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { TmnTableSearchable } from './tmn-table-searchable';
import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-checkbox';
import './table-action-confirmation';
import { SELECT_MODES, MULTI_SELECT_STATUS } from './table-constants';

export class TmnTableSelectable extends TmnTableSearchable {
    constructor() {
        super();
        this.sortable = true;
        this.selectedRowIds = [];
        this.showConfirmNavigateDialog = false;
    }

    static get styles() {
        return [
            ...super.styles,
            css`
                tmn-checkbox {
                    height: 26px;
                    width: 26px;
                }
                th tmn-icon-button {
                    height: 26px;
                    width: 26px;
                    margin-left: -6px;
                    /* --tmn-icon-width: 22px; */
                    --tmn-icon-font-size: 22px;
                }
                th tmn-icon {
                    --tmn-icon-width: 22px;
                }
                tr.selected {
                    background-color: var(--menu-selected-color) !important;
                    cursor: pointer;
                }
                tr.selected {
                    border-bottom: 1px solid var(--app-primary-color, 'black');
                }
                tr.selected.selectable:hover {
                    background-color: var(--menu-select-color-hover) !important;
                }
                tr.selectable:hover {
                    background-color: var(--menu-select-color) !important;
                    cursor: pointer;
                }
                /* .multi-action-bar {
                    --button-border-radius: 4px;
                }
                .multi-action-bar > tmn-grid {
                    padding: 4px 12px;
                    color: var(--app-on-primary-color);
                    background-color: var(--app-primary-color);
                } */
                .main-panel {
                    display: flex;
                    flex: 1 1 100%;
                    flex-direction: row;
                }

                .top-panel {
                    display: flex;
                    flex: 1 1 100%;
                    flex-direction: row;
                }
                .top-bar .selections {
                    padding: 10px 12px;
                    flex: 1;
                    color: var(--app-on-primary-color);
                    background-color: var(--app-primary-color);
                }
            `,
        ];
    }

    static get properties() {
        return {
            selectMode: {
                type: String,
            },
            shouldConfirmOnNavigate: {
                type: Boolean,
            },
            confirmMessage: {
                type: String,
            },
            showConfirmNavigateDialog: {
                type: String,
            },
            pendingEventData: {
                type: Object,
            },
        };
    }

    get multiSelectStatus() {
        // todo review whether search/filtering in table affects this
        const { selectedRowIds, dataRows } = this;
        const someSelected = selectedRowIds?.length > 0;
        const allSelected = selectedRowIds?.length && selectedRowIds.length === dataRows.length;
        if (allSelected) {
            return MULTI_SELECT_STATUS.ALL;
        } else if (someSelected) {
            return MULTI_SELECT_STATUS.SOME;
        } else {
            return MULTI_SELECT_STATUS.NONE;
        }
    }

    getColumnDefs() {
        const baseColumnDefs = super.getColumnDefs();
        if (this.selectMode === SELECT_MODES.MULTI) {
            const checkboxColDef = {
                // consider if this should be a special type
                key: '_select',
                label: '',
                headerFormatter: (col) => {
                    return this.renderMultiSelectCheckBox();
                },
                // hideInDetailMode: true,
                dataFormatter: (value, row) => {
                    return this.renderSelectCheckbox(value, row);
                },
            };

            return [checkboxColDef, ...baseColumnDefs];
        }
        return baseColumnDefs;
    }

    getRows() {
        const baseRows = super.getRows();
        return baseRows.map((row) => {
            const id = row?.metaData?.id;
            if (id) {
                row.metaData.selected = this?.selectedRowIds?.includes(id);
            }
            return {
                ...row,
                metaData: {
                    ...row.metaData,
                },
            };
        });
    }
    // should this have filter panel too really?
    render() {
        return html`${this.renderTopBar()}
            <div class="main-panel">
                ${this.renderDialogs()}
                <div class="scroller">${this.renderTable()}</div>
            </div>`;
    }

    renderTopBar() {
        const { selectedRowIds } = this;

        if (selectedRowIds?.length) {
            return html`<div class="top-bar">
                <div class="selections"><span>${selectedRowIds?.length} selected</span></div>
            </div>`;
        }
        return super.renderTopBar();
    }

    classesForRow(row) {
        const classes = super.classesForRow(row) || {};
        classes.selected = row.metaData.selected;
        classes.selectable = this.selectMode !== SELECT_MODES.NONE;
        return classes;
    }

    renderSelectCheckbox(value, row) {
        const { selected } = row.metaData;
        // for now we are just re-using onRowClick since it gathers up the data
        // if we need to make checkboxes do something different than row select we may need to rethink this
        // <div class="action-icon"></div>
        return html`
            <tmn-checkbox
                .checked=${selected}
                variant="tableAction"
                data-tmn-data-row-id=${row.metaData?.id}
                @tmn-switch-change=${this.onRowClick}>
            </tmn-checkbox>
        `;
        //@tmn-switch-change=${this.onRowClick}
    }

    renderMultiSelectCheckBox() {
        const { multiSelectStatus, selectMode } = this;
        //if (selectMode === SELECT_MODES.MULTI && multiSelectStatus !== MULTI_SELECT_STATUS.NONE) {
        if (selectMode === SELECT_MODES.MULTI) {
            const icon =
                multiSelectStatus === MULTI_SELECT_STATUS.ALL
                    ? 'check_box'
                    : multiSelectStatus === MULTI_SELECT_STATUS.SOME
                    ? 'indeterminate_check_box'
                    : 'check_box_outline_blank';

            return html`
                <tmn-icon-button variant="text" @click="${this.handleToggleAllSelected}">
                    <tmn-icon icon=${icon}></tmn-icon>
                </tmn-icon-button>
            `;
        }
    }

    renderDialogs() {
        return this.renderNaviagationConfirmationDialog();
    }

    renderNaviagationConfirmationDialog() {
        // todo in the future, would it be possible to use a slot to allow something like  "save to continue?"
        // to be passsed down to here?
        const { showConfirmNavigateDialog, confirmMessage } = this;
        return html`<table-action-confirmation
            @tmn-action-confirmed=${this.handleToggleRow}
            @tmn-tool-closed=${this.resetNavigationDialog}
            .confirmMessage=${confirmMessage}
            .open=${showConfirmNavigateDialog}></table-action-confirmation>`;
    }

    resetNavigationDialog() {
        this.showConfirmNavigateDialog = false;
        this.pendingEventData = null;
    }

    handleToggleAllSelected(e) {
        // get ids then iterate
        e.stopPropagation();
        const { multiSelectStatus, selectMode, selectedRowIds } = this;
        if (!selectMode || selectMode === SELECT_MODES.NONE) {
            return;
        }

        const rows = this.getLimitedRows();
        if (!rows?.length) {
            return;
        }
        // if any selected then all off, if none selected then all on (this is how gmail does it)
        const selected = multiSelectStatus === MULTI_SELECT_STATUS.NONE;

        rows.forEach((row) => {
            const { metaData } = row;
            this.toggleRow(metaData.id, metaData.rawData, selected);
        });
    }

    handleRowClick(e, id, rowData) {
        const { selectMode, shouldConfirmOnNavigate } = this;
        if (!selectMode || selectMode === SELECT_MODES.NONE) {
            return;
        }
        this.pendingEventData = { id, rowData };
        if (shouldConfirmOnNavigate) {
            this.showConfirmNavigateDialog = true;
            return;
        }
        this.handleToggleRow(e, id, rowData);
    }

    handleToggleRow(e) {
        const { pendingEventData } = this;
        const { id, rowData } = pendingEventData;
        this.toggleRow(id, rowData);
        this.pendingEventData = null;
    }

    toggleRow(id, rowData, selected) {
        const { selectedRowIds } = this;
        // allow to override the "toggle" with a forced set of selected
        // should consider if we want to keep it this way or provide selected/unselected events
        selected = selected ?? !selectedRowIds?.includes(id);
        if (selected) {
            this.selectRow(id);
        } else {
            this.unselectRow(id);
        }
        const event = new CustomEvent('tmn-row-select-toggle', {
            detail: { selected, id, rowData, selectedRowIds },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    selectRow(id) {
        const { selectedRowIds } = this;
        if (!selectedRowIds?.includes(id)) {
            if (this.selectMode === SELECT_MODES.SINGLE) {
                this.selectedRowIds = [id];
            }
            if (this.selectMode === SELECT_MODES.MULTI) {
                this.selectedRowIds = [...selectedRowIds, id];
            }
        }
    }

    unselectRow(id) {
        const { selectedRowIds } = this;
        if (selectedRowIds.includes(id)) {
            this.selectedRowIds = selectedRowIds.filter((rowId) => rowId !== id);
        }
    }
}

window.customElements.define('tmn-table-selectable', TmnTableSelectable);
